import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

export interface SupplierPerformanceMetric {
  id: string;
  supplier_id: string;
  user_id: string;
  metric_date: string;
  bills_processed: number;
  successful_detections: number;
  failed_detections: number;
  avg_confidence: number;
  avg_processing_time_ms: number;
  error_count: number;
  total_amount_processed: number;
  accuracy_score: number;
  created_at: string;
}

export interface SupplierPatternTest {
  id: string;
  supplier_id: string;
  user_id: string;
  test_name: string;
  test_content: string;
  expected_result: boolean;
  actual_result?: boolean;
  patterns_tested: any;
  confidence_score?: number;
  test_status: 'pending' | 'passed' | 'failed';
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface SupplierIntegrationLog {
  id: string;
  supplier_id: string;
  user_id: string;
  integration_type: string;
  action: string;
  status: 'pending' | 'success' | 'failed';
  request_data?: any;
  response_data?: any;
  error_message?: string;
  execution_time_ms?: number;
  created_at: string;
}

export const useSupplierPerformance = (supplierId?: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const performanceQuery = useQuery({
    queryKey: ['supplier-performance', supplierId, user?.id],
    queryFn: async () => {
      let query = supabase
        .from('supplier_performance_metrics')
        .select('*')
        .eq('user_id', user?.id)
        .order('metric_date', { ascending: false });

      if (supplierId) {
        query = query.eq('supplier_id', supplierId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as SupplierPerformanceMetric[];
    },
    enabled: !!user?.id,
  });

  const patternTestsQuery = useQuery({
    queryKey: ['supplier-pattern-tests', supplierId, user?.id],
    queryFn: async () => {
      let query = supabase
        .from('supplier_pattern_tests')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (supplierId) {
        query = query.eq('supplier_id', supplierId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as SupplierPatternTest[];
    },
    enabled: !!user?.id,
  });

  const integrationLogsQuery = useQuery({
    queryKey: ['supplier-integration-logs', supplierId, user?.id],
    queryFn: async () => {
      let query = supabase
        .from('supplier_integration_logs')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (supplierId) {
        query = query.eq('supplier_id', supplierId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as SupplierIntegrationLog[];
    },
    enabled: !!user?.id,
  });

  const createPatternTestMutation = useMutation({
    mutationFn: async (testData: Omit<SupplierPatternTest, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('supplier_pattern_tests')
        .insert({
          ...testData,
          user_id: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-pattern-tests'] });
      toast({ title: "Success", description: "Pattern test created successfully" });
    },
    onError: (error) => {
      console.error('Error creating pattern test:', error);
      toast({ title: "Error", description: "Failed to create pattern test", variant: "destructive" });
    },
  });

  const runPatternTestMutation = useMutation({
    mutationFn: async (testId: string) => {
      // This would typically call an edge function to run the actual test
      // For now, we'll simulate the test result
      const test = patternTestsQuery.data?.find(t => t.id === testId);
      if (!test) throw new Error('Test not found');

      // Simulate pattern matching logic
      const mockResult = Math.random() > 0.3; // 70% success rate
      const confidence = Math.random() * 100;

      const { data, error } = await supabase
        .from('supplier_pattern_tests')
        .update({
          actual_result: mockResult,
          confidence_score: confidence,
          test_status: mockResult === test.expected_result ? 'passed' : 'failed',
          error_message: mockResult === test.expected_result ? null : 'Pattern did not match expected result'
        })
        .eq('id', testId)
        .eq('user_id', user?.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-pattern-tests'] });
      toast({ title: "Success", description: "Pattern test completed" });
    },
    onError: (error) => {
      console.error('Error running pattern test:', error);
      toast({ title: "Error", description: "Failed to run pattern test", variant: "destructive" });
    },
  });

  const logIntegrationMutation = useMutation({
    mutationFn: async (logData: Omit<SupplierIntegrationLog, 'id' | 'user_id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('supplier_integration_logs')
        .insert({
          ...logData,
          user_id: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-integration-logs'] });
    },
    onError: (error) => {
      console.error('Error logging integration activity:', error);
    },
  });

  return {
    performance: performanceQuery.data || [],
    patternTests: patternTestsQuery.data || [],
    integrationLogs: integrationLogsQuery.data || [],
    isLoading: performanceQuery.isLoading || patternTestsQuery.isLoading || integrationLogsQuery.isLoading,
    error: performanceQuery.error || patternTestsQuery.error || integrationLogsQuery.error,
    createPatternTest: createPatternTestMutation.mutate,
    runPatternTest: runPatternTestMutation.mutate,
    logIntegration: logIntegrationMutation.mutate,
    isCreatingTest: createPatternTestMutation.isPending,
    isRunningTest: runPatternTestMutation.isPending,
  };
};

export const useSupplierHierarchy = (supplierId?: string) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['supplier-hierarchy', supplierId, user?.id],
    queryFn: async () => {
      if (!supplierId) return [];

      const { data, error } = await supabase.rpc('get_supplier_hierarchy', {
        target_supplier_id: supplierId
      });

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !!supplierId,
  });
};